import { datadogRum } from '@datadog/browser-rum'
import { TemplateActionNames } from '../consts/defaultConfigValues/defaultConstants'
import { generateDataDogContext } from './dataDog'
import { isObject } from './validTypes'

/** Which list of `globals.placeOrderFlow` an entry came from. */
export type WorkflowStepListSide = 'actions' | 'afterOrder'

export const DUPLICATE_WORKFLOW_STEP_TITLE =
  'workflow step listed twice in placeOrderFlow'

export const WORKFLOW_STEP_AFTER_PLACE_ORDER_TITLE =
  'workflow step after placeOrder in placeOrderFlow.actions'

/**
 * The lists this page load has already reported, keyed by what the report
 * says (title, side, steps). Not by array identity: both queue starts get
 * their `actions` from `processActions`, which builds a new array on every
 * tap (`resolveQueueArgs` maps the authored list), so the same authored list
 * arrives as a different array each time.
 */
const reportedLists = new Set<string>()

/**
 * Forgets every report made, so a test starts from a fresh page load.
 * @internal
 */
export const resetWorkflowStepListReports = () => {
  reportedLists.clear()
}

/**
 * Reports an authoring mistake in a list once per page load (COM-1296). The
 * queue is restarted on every tap of the CTA, and a mistake in the config is
 * the same mistake on every tap, so one report per distinct mistake is the
 * signal; one per tap would only be volume.
 */
const reportListOnce = (title: string, extraInfo: Record<string, unknown>) => {
  const key = JSON.stringify([title, extraInfo])
  if (reportedLists.has(key)) {
    return
  }
  reportedLists.add(key)
  datadogRum.addError(
    new Error(title),
    generateDataDogContext({ title, extraInfo }),
  )
}

/** The `name` of a list entry, a bare string being a name with no args. */
const entryNameOf = (entry: unknown): unknown => {
  if (typeof entry === 'string') {
    return entry
  }
  if (!isObject(entry)) {
    return undefined
  }
  return (entry as { name?: unknown }).name
}

/**
 * The step key of a `runStep` entry, or `undefined` for anything else: a
 * different action, a bare string (which has no args), or a `runStep` whose
 * `step` is not a string. Those are left to the existing handling.
 */
const stepKeyOf = (entry: unknown): string | undefined => {
  if (!isObject(entry)) {
    return undefined
  }
  const { name, args } = entry as { name?: unknown; args?: unknown }
  if (name !== TemplateActionNames.WORKFLOW_RUN_STEP || !isObject(args)) {
    return undefined
  }
  const { step } = args as { step?: unknown }
  if (typeof step !== 'string') {
    return undefined
  }
  return step
}

/**
 * Drops every `runStep` entry whose step an earlier `runStep` entry in the
 * same list already names (COM-1296). A step listed twice on one side of the
 * order runs once; the duplicate is skipped as if it were not there, and is
 * not a failure. The two sides are deduplicated separately, so a step listed
 * before and after the order runs on both. Entries that are not a `runStep`
 * with a string `step` are kept as they are, so two `openDialog`s still both
 * run.
 *
 * Reports one RUM error when it dropped anything, naming the side and the
 * first duplicate (`step`) and all of them (`steps`), so an author's mistake
 * is visible without one report per entry. Once per distinct report per page
 * load, not per call: the before-queue is deduplicated on every tap.
 *
 * Returns `entries` itself when nothing was dropped. A value that is not an
 * array is returned as is, for the caller's own handling.
 */
export const skipDuplicateWorkflowSteps = <T>(
  entries: T[],
  side: WorkflowStepListSide,
): T[] => {
  if (!Array.isArray(entries)) {
    return entries
  }
  const seen = new Set<string>()
  const duplicates: string[] = []
  const kept = entries.filter((entry) => {
    const step = stepKeyOf(entry)
    if (step === undefined) {
      return true
    }
    if (seen.has(step)) {
      duplicates.push(step)
      return false
    }
    seen.add(step)
    return true
  })
  if (duplicates.length === 0) {
    return entries
  }
  const steps = Array.from(new Set(duplicates))
  reportListOnce(DUPLICATE_WORKFLOW_STEP_TITLE, {
    side,
    step: steps[0],
    steps,
  })
  return kept
}

/**
 * Drops every `runStep` entry that comes after a `CART_PLACE_ORDER` entry in
 * the before-queue (COM-1296), reporting it once per page load.
 *
 * Such a step would start while the create is in flight: `CART_PLACE_ORDER`
 * opens the confirm dialog, whose "yes" starts the create and advances the
 * queue in one go. Failing closed there clears the queue, which resets
 * `isDone`, so the order is placed but the push to `pushAfter` is lost and
 * the shopper sees the failure alert over an order that exists. A step that
 * needs the order belongs in `afterOrder`. Entries that are not a `runStep`
 * are kept wherever they are, as is every entry when no `CART_PLACE_ORDER` is
 * listed. A bare `runStep` string is dropped too: it would run a step with no
 * args. `steps` names each dropped step (`null` for one with no string
 * `step`).
 *
 * Returns `entries` itself when nothing was dropped, and a value that is not
 * an array as is.
 */
export const skipWorkflowStepsAfterPlaceOrder = <T>(entries: T[]): T[] => {
  if (!Array.isArray(entries)) {
    return entries
  }
  let isAfterPlaceOrder = false
  const dropped: Array<string | null> = []
  const kept = entries.filter((entry) => {
    const name = entryNameOf(entry)
    if (name === TemplateActionNames.CART_PLACE_ORDER) {
      isAfterPlaceOrder = true
      return true
    }
    const isStepAfterPlaceOrder =
      isAfterPlaceOrder && name === TemplateActionNames.WORKFLOW_RUN_STEP
    if (!isStepAfterPlaceOrder) {
      return true
    }
    dropped.push(stepKeyOf(entry) ?? null)
    return false
  })
  if (dropped.length === 0) {
    return entries
  }
  reportListOnce(WORKFLOW_STEP_AFTER_PLACE_ORDER_TITLE, { steps: dropped })
  return kept
}

/**
 * The before-queue as both queue starts dispatch it (`usePlaceOrderFlow`'s
 * `placeOrderFlow` and the summary CTA's `continueOrPlaceOrder`): steps after
 * the order dropped, then steps listed twice dropped (COM-1296).
 */
export const prepareBeforeOrderQueue = <T>(entries: T[]): T[] => {
  const beforeOrder = skipWorkflowStepsAfterPlaceOrder(entries)
  return skipDuplicateWorkflowSteps(beforeOrder, 'actions')
}
